import { useContext, useEffect, useMemo, useState } from "react";
import { Empty, Input } from "antd";
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { Button } from "../../../shared";
import { useAdminTags } from "../../../../queries/casino-configuration/games/useAdminTags";
import type { AdminTag } from "../../../../queries/casino-configuration/games/fetchAdminTags";
import TagFormModal from "./pages/TagFormModal";
import CurationEntityName from "../CurationEntityName";
import { useCurationDisplayName } from "../../../../hooks/useCurationDisplayName";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import "./stylesTagsConfiguration.css";

const SEARCH_DEBOUNCE_MS = 200;
const DEFAULT_TAG_COLOR = "#9ea2b8";

function TagsConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_TAGS_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTag, setEditTag] = useState<AdminTag | null>(null);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedSearch(searchQuery.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const { data: tags, isLoading, isError, error } = useAdminTags();
  const { getName } = useCurationDisplayName();
  const tagsList = useMemo<AdminTag[]>(() => tags ?? [], [tags]);

  const filteredTags = useMemo(() => {
    if (!debouncedSearch) return tagsList;
    return tagsList.filter(
      (t) =>
        t.name.toLowerCase().includes(debouncedSearch) ||
        t.id.toLowerCase().includes(debouncedSearch),
    );
  }, [tagsList, debouncedSearch]);

  const stats = useMemo(
    () => ({
      total: tagsList.length,
      seeded: tagsList.filter((t) => t.seeded !== false).length,
    }),
    [tagsList],
  );

  return (
    <>
      <div className="tags-shell">
        <div className="tags-config">
          <div className="tags-header">
            <div>
              <p className="tags-subtitle">
                {AppStrings.CASINO_CONFIG_TAGS_SUBTITLE}
              </p>
            </div>
            <Button
              intent="primary"
              iconLeft={<PlusOutlined />}
              onClick={() => {
                // antd's Modal doesn't trap focus; a keyboard user could
                // otherwise Tab out of an open edit modal and hit this button,
                // ending up with both modals rendered at once. Closing the
                // edit modal here makes the two states mutually exclusive.
                setEditTag(null);
                setCreateOpen(true);
              }}
              data-testid="tags-new-button"
            >
              {AppStrings.CASINO_CONFIG_TAGS_NEW_BUTTON}
            </Button>
          </div>

          <div className="tags-controls">
            <Input
              className="tags-search"
              prefix={<SearchOutlined />}
              placeholder={
                AppStrings.CASINO_CONFIG_TAGS_SEARCH_PLACEHOLDER
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
            <div className="tags-stats">
              <span>
                <b>{stats.total}</b>{" "}
                {AppStrings.CASINO_CONFIG_TAGS_COUNT_SUFFIX}
              </span>
              <span className="tags-stats-sep" aria-hidden>
                &middot;
              </span>
              <span>
                <b>{stats.seeded}</b>{" "}
                {AppStrings.CASINO_CONFIG_TAGS_SEEDED_SUFFIX}
              </span>
            </div>
          </div>

          <div className="tags-grid" data-testid="tags-grid">
            {isLoading ? (
              <div className="tags-loader">
                <RebetLoader height="64px" width="64px" />
              </div>
            ) : isError ? (
              <div className="tags-empty">
                <Empty
                  description={
                    (error as Error)?.message ||
                    AppStrings.CASINO_CONFIG_TAGS_LOAD_ERROR
                  }
                />
              </div>
            ) : filteredTags.length === 0 ? (
              <div className="tags-empty">
                <Empty
                  description={AppStrings.CASINO_CONFIG_TAGS_EMPTY}
                />
              </div>
            ) : (
              filteredTags.map((tag) => {
                const color = tag.color || DEFAULT_TAG_COLOR;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    className="tags-row"
                    onClick={() => {
                      // Mutual exclusion w/ the "New tag" modal — see the
                      // matching `setEditTag(null)` in that button's handler.
                      setCreateOpen(false);
                      setEditTag(tag);
                    }}
                    data-testid={`tag-row-${tag.id}`}
                    aria-label={formatString(
                      AppStrings.CASINO_CONFIG_GAMES_EDIT_ARIA,
                      { name: getName(tag) },
                    )}
                  >
                    <span
                      className="tags-row-pill"
                      style={{
                        background: `${color}22`,
                        color,
                        borderColor: color,
                      }}
                    >
                      <CurationEntityName
                        entity={tag}
                        displayName={getName(tag)}
                      />
                    </span>
                    <span className="tags-row-meta">
                      <span className="tags-row-id">{tag.id}</span>
                      <span className="tags-row-color">
                        {color}
                      </span>
                    </span>
                    {tag.seeded !== false ? (
                      <span className="tags-row-seeded">
                        {AppStrings.CASINO_CONFIG_TAGS_SEEDED_BADGE}
                      </span>
                    ) : null}
                    {/*
                     * The whole row is clickable, but the pencil icon adds a
                     * clear visual affordance. We render it as a non-interactive
                     * `<span>` (not a nested `<button>`) so click events bubble
                     * to the row's click handler — keeps the contract simple
                     * and avoids nested-interactive-element a11y warnings.
                     */}
                    <span
                      className="tags-row-edit-icon"
                      aria-hidden="true"
                      data-testid={`tag-row-edit-${tag.id}`}
                    >
                      <EditOutlined />
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <TagFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <TagFormModal
        open={editTag !== null}
        tag={editTag}
        onClose={() => setEditTag(null)}
      />
    </>
  );
}

export default TagsConfigurationPage;
